import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { TrendingUp, Users, PhoneCall, Clock, ShieldAlert, PieChart } from 'lucide-react';
import { Button } from './ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart as RechartsPie, Pie, Cell } from 'recharts';
import axios from 'axios';
import Logo from './Logo';
import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const Analytics = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [token]);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/calls/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.log('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div className="bg-gray-900 border border-white/10 rounded-lg px-3 py-2 shadow-xl">
          <p className="text-white text-sm font-medium">{label}</p>
          <p className="text-purple-400 text-sm">{payload[0].value} calls</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628] flex items-center justify-center">
        <p className="text-gray-400">Loading analytics...</p>
      </div>
    );
  }

  const stats = [
    { label: 'Total Calls', value: data?.total_calls || 0, icon: PhoneCall, color: 'purple' },
    { label: 'Leads Captured', value: data?.total_leads || 0, icon: Users, color: 'teal' },
    { label: 'Conversion Rate', value: `${data?.conversion_rate || 0}%`, icon: TrendingUp, color: 'blue' },
    { label: 'Avg. Daily Calls', value: data?.avg_daily || 0, icon: Clock, color: 'emerald' },
    { label: 'Spam Filtered', value: data?.spam_count || 0, icon: ShieldAlert, color: 'red' }
  ];

  const colorMap = { purple: 'bg-purple-500/20 text-purple-400', teal: 'bg-teal-500/20 text-teal-400', blue: 'bg-blue-500/20 text-blue-400', emerald: 'bg-emerald-500/20 text-emerald-400', red: 'bg-red-500/20 text-red-400' };

  // Intent breakdown for pie chart
  const INTENT_COLORS = { lead: '#10b981', appointment: '#3b82f6', inquiry: '#f59e0b', support: '#a855f7', spam: '#ef4444', other: '#6b7280', unknown: '#4b5563' };
  const intentData = data?.intent_breakdown
    ? Object.entries(data.intent_breakdown).map(([key, val]) => ({ name: key, value: val }))
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]" data-testid="analytics-page">
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Logo linkTo="/dashboard" />
          <div className="h-6 w-px bg-white/10" />
          <h1 className="text-xl font-bold text-white">Analytics</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="analytics-stats">
          {stats.map((s) => (
            <Card key={s.label} className="bg-gray-900/50 border-white/10">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorMap[s.color]}`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">{s.label}</p>
                  <p className="text-xl font-bold text-white" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>{s.value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Daily Call Volume Chart */}
        <Card className="bg-gray-900/50 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-lg">Call Volume (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64" data-testid="daily-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data?.daily_volume || []}>
                  <defs>
                    <linearGradient id="callGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis
                    dataKey="date"
                    stroke="#64748b"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => v.slice(5)}
                  />
                  <YAxis stroke="#64748b" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="calls" stroke="#a855f7" fillOpacity={1} fill="url(#callGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Hourly Distribution + Intent Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-gray-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg">Busiest Hours</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48" data-testid="hourly-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data?.hourly_distribution || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="hour" stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}:00`} />
                    <YAxis stroke="#64748b" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="calls" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg">Call Intent Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              {intentData.length > 0 ? (
                <div className="h-48 flex items-center" data-testid="intent-chart">
                  <ResponsiveContainer width="50%" height="100%">
                    <RechartsPie>
                      <Pie data={intentData} cx="50%" cy="50%" outerRadius={70} dataKey="value" stroke="none">
                        {intentData.map((entry, i) => (
                          <Cell key={i} fill={INTENT_COLORS[entry.name] || '#6b7280'} />
                        ))}
                      </Pie>
                    </RechartsPie>
                  </ResponsiveContainer>
                  <div className="space-y-2 flex-1">
                    {intentData.map((entry) => (
                      <div key={entry.name} className="flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: INTENT_COLORS[entry.name] || '#6b7280' }} />
                        <span className="text-gray-400 capitalize">{entry.name}</span>
                        <span className="text-white font-medium ml-auto">{entry.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-gray-500 text-sm">
                  No call data yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Analytics;
